"use client";

import { Currency } from "@finbackoffice/enums";
import { FC, useContext, useMemo } from "react";
import classNames from "classnames";
import { ICurrencyFormatConfig } from "@finbackoffice/fe-core";
import { convertToCurrencyFormatterConfig, formatAmount } from "@finbackoffice/site-core";
import { ConfigContextV2 } from "contexts";
import styles from "./currency-formater.module.sass";

type IProps = {
    currency: Currency | "";
    amount: string;
    withCode?: boolean;
    withSymbol?: boolean;
    testId?: string;
    configOverride?: Partial<ICurrencyFormatConfig>;
};

export const CurrencyFormatter: FC<IProps> = ({
    currency,
    amount,
    withCode = true,
    withSymbol,
    testId,
    configOverride,
}) => {
    const { siteConfigReady, sitePaymentsCurrencyConfig } = useContext(ConfigContextV2);
    const config = useMemo(() => {
        const currencyConfig = sitePaymentsCurrencyConfig?.[currency as Currency];
        return currencyConfig
            ? convertToCurrencyFormatterConfig(currency as Currency, currencyConfig)
            : null;
    }, [currency, sitePaymentsCurrencyConfig]);

    const format: ICurrencyFormatConfig | null = useMemo(
        () =>
            config
                ? {
                      ...config,
                      ...configOverride,
                  }
                : null,
        [configOverride, config],
    );

    const renderAmount = useMemo(
        () => formatAmount({ format, amount, withCode, withSymbol: withSymbol || false }),
        [amount, format, withCode, withSymbol],
    );

    return siteConfigReady ? (
        <span
            dir="ltr"
            data-testid={testId}
            className={classNames(
                styles.currency,
                withSymbol &&
                    format &&
                    (format.symbol
                        ? "currency-with-symbol"
                        : `base-icon-currency-${currency?.toLowerCase()}`),
            )}>
            {renderAmount}
        </span>
    ) : null;
};
